import { CommonModule } from '@angular/common';
import { Component, computed, ElementRef, EventEmitter, HostListener, inject, Input, OnChanges, Output, signal, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslocoModule } from '@jsverse/transloco';
import { IconComponent } from './icon/icon.component';

interface HierarchyNode {
  id: number;
  category_name: string;
  children?: HierarchyNode[];
}

@Component({
  selector: 'app-cascader-select',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslocoModule, IconComponent],
  templateUrl: './cascader-select.component.html',
  styleUrl: './cascader-select.component.scss'
})

export class CascaderSelectComponent implements OnChanges {
  @Input({ required: true }) categories!: HierarchyNode[];
  @Input() selectedIds: number[] = [];
  @Input() label: string = 'Category';
  @Input() customClass: string = '';
  @Input() disabled: boolean = false;
  @Output() save = new EventEmitter<number[]>();
  
  private selectedIdsSignal = signal<number[]>([]);
  private elementRef = inject(ElementRef);

  isOpen = signal(false);
  viewLevel = signal(0);
  history = signal<HierarchyNode[]>([]);
  
  selectedPathNames = computed(() => {
    const ids = this.selectedIdsSignal();
    if (!ids || ids.length === 0 || !this.categories || this.categories.length === 0) return [];
    
    const names: string[] = [];
    let currentNodes = this.categories.filter(c => c != null);
    
    for (const id of ids) {
      const found = currentNodes.find(n => n?.id === id);
      if (found) {
        names.push(found.category_name);
        currentNodes = (found.children || []).filter(c => c != null);
      } else {
        break;
      }
    }
    return names;
  });
  
  
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedIds']) {
      this.selectedIdsSignal.set(this.selectedIds || []);
    }
  }
  

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.close();
    }
  }

  
  /**
   * Toggles the open state of the dropdown and resets the view if it's closed.
   */
  toggleOpen(): void {
    if (this.disabled) {
      return;
    }
    this.isOpen.update(v => !v);
    if (!this.isOpen()) {
        this.reset();
    }
  }


  /**
   * Closes the dropdown and resets the view.
   */
  close(): void {
    this.isOpen.set(false);
    this.reset();
  }


 /**
  * Resets the view by setting the view level to 0 and clearing the history.
  */
  reset(): void {
      setTimeout(() => {
        this.viewLevel.set(0);
        this.history.set([]);
      }, 200);
  }



  /**
   * Handles the selection of a category.
   * @param event The event object.
   * @param item The selected category.
   */
  handleSelection(event: Event, item: HierarchyNode): void {
    event.stopPropagation();
    if (this.disabled) {
      return;
    }
    if (item.children && item.children.length > 0) {
      this.history.update(h => [...h, item]);
      this.viewLevel.update(l => l + 1);
    } else {
      const historyIds = this.history().map(h => h.id);
      this.save.emit([...historyIds, item.id]);
      this.close();
    }
  }

  
  
  
  /**
   * Handles the back action in the dropdown.
   * @param e The event object.
   */
  handleBack(e: Event): void {
    e.stopPropagation();
    this.history.update(h => h.slice(0, -1));
    this.viewLevel.update(l => l - 1);
  }



  /**
   * Gets the header title based on the current view level and history.
   * @returns The header title.
   */
  getHeaderTitle(): string {
    const level = this.viewLevel();
    const hist = this.history();

    if (level === 0) {
      const validCategories = this.categories?.filter(c => c != null) || [];
      return validCategories[level]?.category_name || '';
    }
    return hist[level - 1]?.category_name || '';
  }

  
  
  /**
   * Gets the current items based on the view level and history.
   * @returns The current items.
   */
  currentItems(): HierarchyNode[] {
      const level = this.viewLevel();
      const hist = this.history();
      if (level === 0) {
        return (this.categories || []).filter(c => c != null);
      }
      return (hist[level - 1]?.children || []).filter(c => c != null);
  }

  

  /**
   * Gets the breadcrumb path based on the history.
   * @returns The breadcrumb path.
   */
  getBreadcrumbPath(): string {
    const hist = this.history();
    if (hist.length === 0) return '';
    return hist.filter(h => h != null).map(h => h?.category_name || '').join(' > ');
  }


  /**
   * Gets the leaf of the selected path.
   * @returns The leaf of the selected path.
   */
  leaf = computed(() => {
    const names = this.selectedPathNames();
    return names.length > 0 ? names[names.length - 1] : "";
  });



  /**
   * Gets the parent string of the selected path.
   * @returns The parent string of the selected path.
   */
  parentString = computed(() => {
    const names = this.selectedPathNames();
    return names.length > 1 ? names.slice(0, -1).join(' > ') : "";
  });

  hasSelection = computed(() => this.selectedIdsSignal().length > 0);

  clearSelection(event: Event): void {
    event.stopPropagation();
    if (this.disabled) {
      return;
    }
    this.save.emit([]);
    this.close();
  }
}
